import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { AdminUser } from '@/types/admin-user'
import type { MfaSetupResponse, MfaVerifySetupResponse, RecoveryCodesResponse } from '@/types/mfa'
import type { AdminSession } from '@/types/session'
import * as authApi from '@/api/auth'
import * as sessionsApi from '@/api/sessions'

// ---------------------------------------------------------------------------
// Types — re-exported for convenience
// ---------------------------------------------------------------------------

export type { AdminUser }
export type { AdminRole } from '@/types/admin-user'
export type { AdminSession }

// ---------------------------------------------------------------------------
// In-memory token storage (NEVER localStorage / sessionStorage)
// The access token lives in a plain variable for security (not enumerable
// on any reactive proxy). A reactive boolean shadow tracks "has token" so
// that Vue computed properties can react to changes.
//
// The refresh token is stored as an HttpOnly cookie by the backend —
// the frontend NEVER sees or stores it. The browser sends it automatically
// on refresh requests (withCredentials: true).
// ---------------------------------------------------------------------------
let _accessToken: string | null = null

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAdminAuthStore = defineStore('admin-auth', () => {
  // ---- State ---------------------------------------------------------------
  const adminUser = ref<AdminUser | null>(null)
  const loading = ref(false)
  const isMfaRequired = ref(false)
  const isMfaSetupRequired = ref(false)
  const mfaSessionToken = ref<string | null>(null)

  // Session management state
  const sessions = ref<AdminSession[]>([])
  const sessionsLoading = ref(false)

  // Reactive shadow — tracks whether _accessToken is set so computed props react
  const _hasToken = ref(false)

  // Whether initAuth() has completed (regardless of success/failure)
  const _initialized = ref(false)

  // ---- Getters -------------------------------------------------------------

  /**
   * True only when:
   * - We have an access token in memory
   * - MFA challenge has been completed (isMfaRequired is false)
   * - No pending MFA setup
   */
  const isAuthenticated = computed(
    () => _hasToken.value && !isMfaRequired.value && !isMfaSetupRequired.value,
  )

  // ---- Actions -------------------------------------------------------------

  /**
   * Step 1: Authenticate with email + password.
   * If the backend returns `requires_mfa: true`, the store enters the MFA
   * challenge state and the caller should redirect to the MFA verification view.
   */
  async function login(email: string, password: string): Promise<void> {
    loading.value = true
    try {
      const response = await authApi.postLogin(email, password)

      if (response.requires_mfa) {
        // MFA challenge required — store the mfa_token for the next step
        isMfaRequired.value = true
        mfaSessionToken.value = response.mfa_token ?? null
        // Do NOT set access token yet — login is incomplete
        return
      }

      // No MFA required — access token in body, refresh token set as HttpOnly cookie by backend
      _accessToken = response.access_token ?? null
      _hasToken.value = !!_accessToken
      await fetchCurrentUser()
    } finally {
      loading.value = false
    }
  }

  /**
   * Step 2: Verify MFA code to complete authentication.
   * Called after `login()` when `isMfaRequired` is true.
   */
  async function verifyMfa(code: string): Promise<void> {
    if (!mfaSessionToken.value) {
      throw new Error('No MFA session token — call login() first')
    }

    loading.value = true
    try {
      // Access token in body, refresh token set as HttpOnly cookie by backend
      const response = await authApi.postMfaVerify(code, mfaSessionToken.value)
      _accessToken = response.access_token
      _hasToken.value = true

      // Clear MFA challenge state
      isMfaRequired.value = false
      mfaSessionToken.value = null

      // Fetch the admin user profile now that we're fully authenticated
      await fetchCurrentUser()
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch the current admin user profile.
   * Also detects if MFA setup is required (mfa_enabled === false).
   */
  async function fetchCurrentUser(): Promise<void> {
    const user = await authApi.getCurrentUser()
    adminUser.value = user

    // If MFA is not yet enabled, the user must set it up before proceeding
    if (!user.mfa_enabled) {
      isMfaSetupRequired.value = true
    }
  }

  /**
   * Initiate MFA setup — returns the TOTP secret and QR URI.
   * Called when `isMfaSetupRequired` is true (first-time enrollment).
   */
  async function setupMfa(): Promise<MfaSetupResponse> {
    loading.value = true
    try {
      return await authApi.postMfaSetup()
    } finally {
      loading.value = false
    }
  }

  /**
   * Verify MFA setup by validating the first TOTP code.
   * Returns the message and recovery codes that the user should store securely.
   */
  async function verifyMfaSetup(code: string): Promise<MfaVerifySetupResponse> {
    loading.value = true
    try {
      const result = await authApi.postMfaVerifySetup(code)
      isMfaSetupRequired.value = false

      // Update user to reflect MFA is now enabled
      if (adminUser.value) {
        adminUser.value = { ...adminUser.value, mfa_enabled: true }
      }

      return result
    } finally {
      loading.value = false
    }
  }

  /**
   * Silently refresh the access token.
   * The refresh token is sent automatically as an HttpOnly cookie by the browser.
   */
  async function refreshToken(): Promise<void> {
    const response = await authApi.postRefresh()
    _accessToken = response.access_token
    _hasToken.value = true
  }

  /**
   * Logout — clear all in-memory auth state and notify the backend.
   */
  async function logout(): Promise<void> {
    try {
      // Best-effort server-side logout
      if (_accessToken) {
        await authApi.postLogout()
      }
    } catch {
      // Swallow errors — we clear state regardless
    } finally {
      // SECURITY: Always clear in-memory access token
      // Refresh token cookie is cleared by the backend's logout response (Set-Cookie with Max-Age=0)
      _accessToken = null
      _hasToken.value = false
      adminUser.value = null
      isMfaRequired.value = false
      isMfaSetupRequired.value = false
      mfaSessionToken.value = null
      loading.value = false
    }
  }

  /**
   * Attempt to restore the session on page load / app startup.
   * Calls POST /auth/refresh — the browser automatically sends the HttpOnly
   * refresh cookie. If the cookie exists and is valid, we get a new access
   * token and the user stays logged in across page refreshes.
   *
   * Must be called once before the app mounts. The router guard checks
   * `_initialized` to avoid redirecting to login before this completes.
   */
  async function initAuth(): Promise<void> {
    if (_initialized.value) return
    try {
      await refreshToken()
      // Token refresh succeeded — try to load user profile
      try {
        await fetchCurrentUser()
      } catch {
        // Profile load failed but token is still valid — user can proceed
      }
    } catch {
      // No valid refresh cookie — user needs to log in
      _accessToken = null
      _hasToken.value = false
    } finally {
      _initialized.value = true
    }
  }

  // ---- Session Management ---------------------------------------------------

  /**
   * Fetch all active sessions for the current admin user.
   * SECURITY: Shows admin user's own sessions only (unless super_admin).
   */
  async function fetchSessions(): Promise<void> {
    sessionsLoading.value = true
    try {
      sessions.value = await sessionsApi.getSessions()
    } finally {
      sessionsLoading.value = false
    }
  }

  /**
   * Revoke (terminate) a single session by its ID.
   * After revocation, the sessions list is refreshed.
   */
  async function revokeSession(sessionId: string): Promise<void> {
    await sessionsApi.revokeSession(sessionId)
    // Remove the revoked session from the local list
    sessions.value = sessions.value.filter((s) => s.id !== sessionId)
  }

  /**
   * Revoke all sessions except the current one.
   * After revocation, only the current session remains in the list.
   */
  async function revokeAllSessions(): Promise<void> {
    await sessionsApi.revokeAllOtherSessions()
    // Keep only the current session in the local list
    sessions.value = sessions.value.filter((s) => s.is_current)
  }

  /**
   * Returns the current in-memory access token (for attaching to requests).
   * Intentionally NOT reactive — callers should use getAccessToken() right
   * before each request so they always get the latest value.
   */
  function getAccessToken(): string | null {
    return _accessToken
  }

  /**
   * Set the access token directly. Used for testing only.
   * In production, the refresh token is an HttpOnly cookie managed by the backend.
   * @internal
   */
  function _setTokens(access: string, _refresh?: string): void {
    _accessToken = access
    _hasToken.value = true
  }

  /**
   * Reset all auth state. Exposed for testing only.
   * @internal
   */
  function _reset(): void {
    _accessToken = null
    _hasToken.value = false
    _initialized.value = false
    adminUser.value = null
    isMfaRequired.value = false
    isMfaSetupRequired.value = false
    mfaSessionToken.value = null
    loading.value = false
    sessions.value = []
    sessionsLoading.value = false
  }

  return {
    // state
    adminUser,
    loading,
    isMfaRequired,
    isMfaSetupRequired,
    mfaSessionToken,
    sessions,
    sessionsLoading,

    // getters
    isAuthenticated,

    // actions
    initAuth,
    login,
    verifyMfa,
    logout,
    refreshToken,
    fetchCurrentUser,
    setupMfa,
    verifyMfaSetup,
    fetchSessions,
    revokeSession,
    revokeAllSessions,
    getAccessToken,
    _setTokens,
    _reset,
  }
})
